import { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listEnterprises, type Enterprise } from "@/services/platformEnterprisesService";
import { formatDisplayDate } from "@/lib/dateFormat";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";

const QUERY_KEY = ["platform", "enterprises"] as const;
const COLUMN_COUNT = 6;

function buildRowActions(
  enterprise: Enterprise,
  navigate: (path: string) => void,
  t: (key: string) => string,
): TableAction[] {
  return [
    {
      label: t("platformEnterprises.viewDetails"),
      icon: Eye,
      onClick: () => navigate(`/platform/enterprises/${enterprise.id}`),
    },
    {
      label: t("platformEnterprises.editAction"),
      icon: Pencil,
      onClick: () => navigate(`/platform/enterprises/${enterprise.id}/edit`),
    },
  ];
}

export function EnterprisesListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pagination = usePaginationFromUrl();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listEnterprises,
  });

  const enterprises = listQuery.data ?? [];

  useEffect(() => {
    if (listQuery.isError) {
      toast.error(t("platformEnterprises.loadError"));
    }
  }, [listQuery.isError, t]);

  useEffect(() => {
    pagination.setTotal(enterprises.length);
  }, [enterprises.length, pagination]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return enterprises.slice(start, start + pagination.limit);
  }, [enterprises, pagination.page, pagination.limit]);

  const loading = listQuery.isLoading;
  const totalRows = enterprises.length;

  const handleRefresh = (): void => {
    setIsRefreshing(true);
    void listQuery.refetch().finally(() => setIsRefreshing(false));
  };

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead>{t("platformEnterprises.colName")}</TableHead>
        <TableHead>{t("platformEnterprises.colSubdomain")}</TableHead>
        <TableHead>{t("platformEnterprises.colActive")}</TableHead>
        <TableHead>{t("platformEnterprises.colCreatedAt")}</TableHead>
        <TableHead className="w-[80px]">{t("platformEnterprises.colActions")}</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("platformEnterprises.listTitle")}
        description={t("platformEnterprises.listDescription")}
        actions={
          <Button
            type="button"
            onClick={() => navigate("/platform/enterprises/new")}
            className="transition-colors duration-150"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("platformEnterprises.newButton")}
          </Button>
        }
      />

      <TableCard className="mt-4">
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : (
          <>
            {totalRows === 0 ? (
              <ResponsiveListSplit
                table={
                  <Table>
                    {tableHeader}
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} className="h-32">
                          <EmptyState
                            title={t("platformEnterprises.emptyTitle")}
                            message={t("platformEnterprises.emptyMessage")}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="flex min-h-[40vh] items-center justify-center p-4">
                    <EmptyState
                      title={t("platformEnterprises.emptyTitle")}
                      message={t("platformEnterprises.emptyMessage")}
                    />
                  </div>
                }
              />
            ) : (
              <ResponsiveListSplit
                table={
                  <Table>
                    {tableHeader}
                    <TableBody>
                      {paginatedRows.map((row) => (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer transition-colors duration-150 hover:bg-muted/50"
                          onClick={() =>
                            navigate(`/platform/enterprises/${row.id}`)
                          }
                        >
                          <TableCell className="font-medium text-foreground">
                            {row.name}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {row.subdomain}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              variant={row.isActive ? "success" : "info"}
                            >
                              {row.isActive
                                ? t("platformEnterprises.activeYes")
                                : t("platformEnterprises.activeNo")}
                            </StatusBadge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDisplayDate(row.createdAt)}
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TableActionsMenu
                              actions={buildRowActions(row, navigate, t)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="divide-y divide-border">
                    {paginatedRows.map((row) => (
                      <DataListCardRow key={row.id}>
                        <div className="flex items-start justify-between gap-3">
                          <p
                            className="min-w-0 flex-1 cursor-pointer font-semibold leading-snug text-foreground"
                            onClick={() =>
                              navigate(`/platform/enterprises/${row.id}`)
                            }
                          >
                            {row.name}
                          </p>
                          <div className="shrink-0">
                            <TableActionsMenu
                              actions={buildRowActions(row, navigate, t)}
                            />
                          </div>
                        </div>
                        <div className="mt-3 space-y-3">
                          <DataListCardField
                            label={t("platformEnterprises.colSubdomain")}
                          >
                            <span className="text-muted-foreground">
                              {row.subdomain}
                            </span>
                          </DataListCardField>
                          <DataListCardField
                            label={t("platformEnterprises.colActive")}
                          >
                            <StatusBadge
                              variant={row.isActive ? "success" : "info"}
                            >
                              {row.isActive
                                ? t("platformEnterprises.activeYes")
                                : t("platformEnterprises.activeNo")}
                            </StatusBadge>
                          </DataListCardField>
                          <DataListCardField
                            label={t("platformEnterprises.colCreatedAt")}
                          >
                            <span className="text-muted-foreground">
                              {formatDisplayDate(row.createdAt)}
                            </span>
                          </DataListCardField>
                        </div>
                      </DataListCardRow>
                    ))}
                  </div>
                }
              />
            )}
            {totalRows > 0 ? (
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("platformEnterprises.rowsPerPage")}
                pageOfLabel={t("platformEnterprises.pageOf", {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  ),
                  total: pagination.total,
                })}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
            ) : null}
          </>
        )}
      </TableCard>
    </div>
  );
}
